import type { ParentChildAssignment } from '@repo/types/parent';

import { useGetChildAssignment } from '@repo/api/parent/useGetChildAssignment';
import { format, parseISO } from 'date-fns';
import { TriangleAlertIcon } from 'lucide-react';

import { getParentAssignmentState, ParentAssignmentStatusBadge } from '@/components/features/parent/ParentAssignmentStatus';
import { ParentGradeValue } from '@/components/features/parent/ParentGradeValue';
import { ParentRichText } from '@/components/features/parent/ParentRichText';
import { ParentStat } from '@/components/features/parent/ParentStat';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useParentDetailTarget } from '@/hooks/parent';

/** Same wording as the card, so the row and the dialog read as one record. */
const DATE_FORMAT = 'd MMM yyyy, H:mm';

/**
 * Everything the assignment row cannot fit: the dates around the deadline and
 * the two descriptions.
 *
 * The descriptions are the reason this exists — they are authored HTML of
 * unbounded length, so rendering them in a list would make every row a different
 * height and bury the one figure a parent came for.
 *
 * Which assignment it shows comes from the URL and is fetched by id, so a link
 * opens it whether or not the row it belongs to is on the page. One of these per
 * page is enough; the rows only write the link.
 */
export function ParentAssignmentDialog() {
  const target = useParentDetailTarget('assignment');
  const assignmentId = Number(target?.itemId);

  const { data, isPending, isError, errorMessage } = useGetChildAssignment(target?.childId, Number.isInteger(assignmentId) ? assignmentId : undefined);

  return (
    <Dialog
      open={Boolean(target)}
      onOpenChange={(open) => {
        if (!open) target?.close();
      }}
    >
      <DialogContent className={'bg-card sm:max-w-lg'}>
        <DialogBody>
          <DialogHeader>
            <DialogTitle>{data?.name ?? 'Zadanie'}</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Szczegóły zadania</DialogDescription>

          {isPending ? (
            <div className={'flex flex-col gap-3 px-4 pb-4'}>
              <Skeleton className={'h-10 w-full'} />
              <Skeleton className={'h-24 w-full'} />
            </div>
          ) : isError || !data ? (
            <div className={'px-4 pb-4'}>
              <Alert variant={'destructive'}>
                <TriangleAlertIcon />
                <AlertDescription>{errorMessage?.detail ?? 'Nie udało się pobrać zadania.'}</AlertDescription>
              </Alert>
            </div>
          ) : (
            <AssignmentDetails assignment={data} />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface AssignmentDetailsProps {
  assignment: ParentChildAssignment;
}

function AssignmentDetails(assignmentDetailsProps: AssignmentDetailsProps) {
  const { assignment } = assignmentDetailsProps;
  const { desc, available } = assignment;

  const state = getParentAssignmentState(assignment);
  const { submission, dueDate } = state;

  const availableDate = available ? parseISO(available) : null;
  // A submission always carries its own timestamp, so "handed in on" is only
  // absent when nothing was handed in.
  const submittedDate = submission ? parseISO(submission.createdAt) : null;
  const feedback = submission?.feedback?.overall ?? null;

  return (
    <div className={'flex flex-col gap-5 overflow-auto px-4 pb-4'}>
      <div className={'flex flex-wrap items-start gap-x-6 gap-y-3'}>
        {dueDate ? <ParentStat label={'Termin'}>{format(dueDate, DATE_FORMAT)}</ParentStat> : null}

        {availableDate ? <ParentStat label={'Dostępne od'}>{format(availableDate, DATE_FORMAT)}</ParentStat> : null}

        {submittedDate ? <ParentStat label={'Oddano'}>{format(submittedDate, DATE_FORMAT)}</ParentStat> : null}

        <ParentGradeValue grade={submission?.grade ?? null} />

        <ParentStat label={'Status'}>
          <ParentAssignmentStatusBadge state={state} />
        </ParentStat>
      </div>

      {desc ? (
        <section className={'flex flex-col gap-1.5'}>
          <h3 className={'text-muted-foreground text-xs font-medium'}>Opis zadania</h3>
          <ParentRichText content={desc} />
        </section>
      ) : null}

      {feedback ? (
        <section className={'flex flex-col gap-1.5'}>
          <h3 className={'text-muted-foreground text-xs font-medium'}>Komentarz nauczyciela</h3>
          <ParentRichText content={feedback} />
        </section>
      ) : null}
    </div>
  );
}
